import {AlertIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {useMergeabilityData, type MergeabilityData} from './use-mergeability-data'

// Entry statuses that mean the entry itself has an unresolved problem, rather than
// merely being blocked by another entry further down the stack.
const BLOCKING_ENTRY_STATUSES = new Set(['NOT_READY', 'DRAFT', 'CLOSED', 'MERGE_CONFLICTS', 'NEEDS_REBASE'])

// `stackEntries` is ordered top (upstack) to base (downstack); the last entry with
// an unresolved status is the one closest to the base of the stack.
function findBlockingDownstackPullRequest(data: MergeabilityData | null) {
  const entries = data?.stackSection?.stackEntries
  if (!entries) return undefined

  const currentIndex = entries.findIndex(entry => entry.position === 'CURRENT')
  const currentStatus = entries[currentIndex]?.effectiveEntryStatus
  if (currentIndex === -1 || !(currentStatus === 'BLOCKED_DOWNSTACK' || currentStatus === 'NOT_READY')) return undefined

  return entries
    .slice(currentIndex + 1)
    .findLast(entry => entry.effectiveEntryStatus && BLOCKING_ENTRY_STATUSES.has(entry.effectiveEntryStatus))?.pull
}

export function shouldShowBlockedByDownstackPullRequest(data: MergeabilityData | null): boolean {
  return (
    !!data &&
    !!data.stackSection?.shouldRender &&
    !data.isClosedOrMerged &&
    !data.mergeBoxPullRequest.isDraft &&
    !!findBlockingDownstackPullRequest(data)
  )
}

// Renders when the only thing keeping this pull request from merging is an
// unresolved problem on a pull request further down the stack.
export function BlockedByDownstackPullRequest({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useMergeabilityData()
  const blockingPullRequest = findBlockingDownstackPullRequest(data)

  if (!shouldShowBlockedByDownstackPullRequest(data) || !blockingPullRequest) return null

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={AlertIcon} variant="danger" />}
      title="Blocked by a downstack pull request"
      itemName="blocked-by-downstack-pull-request"
      variant={variant}
      description={
        <>
          Resolve the issues in{' '}
          <Link inline className="Link--inTextBlock" href={blockingPullRequest.url}>
            #{blockingPullRequest.number}
          </Link>{' '}
          before merging this pull request.
        </>
      }
    />
  )
}
